import React, { useState } from 'react';
import T from '../theme';

// ===== STATUS PILL =====
export function StatusPill({ status }) {
  const styles = {
    new: { bg: T.yellowSoft, color: '#92400E', border: '#FCD34D', label: 'Yangi' },
    active: { bg: T.greenSoft, color: '#065F46', border: '#6EE7B7', label: 'Faol' },
    inactive: { bg: T.coralSoft, color: '#991B1B', border: '#FCA5A5', label: 'Nofaol' },
    expired: { bg: '#F3F4F6', color: '#6B7280', border: '#D1D5DB', label: 'Muddat tugagan' },
  };
  const s = styles[status] || styles.inactive;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`,
    }}>{s.label}</span>
  );
}

// ===== BATTERY BAR =====
export function BattBar({ level }) {
  const color = level > 50 ? T.blue : level > 20 ? T.yellow : T.coral;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <div style={{ width: 44, height: 8, borderRadius: 4, background: '#E2E8F0' }}>
        <div style={{ width: `${level}%`, height: '100%', borderRadius: 4, background: color, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, fontFamily: T.mono }}>{level}%</span>
    </div>
  );
}

// ===== SECURITY CODE =====
export function SecCode({ code, big }) {
  const formatted = code ? `${code.slice(0, 3)}-${code.slice(3, 6)}-${code.slice(6)}` : '---';
  return (
    <code style={{
      fontFamily: T.mono, fontSize: big ? 24 : 13, fontWeight: 700,
      color: T.blue, letterSpacing: big ? 4 : 2,
    }}>{formatted}</code>
  );
}

// ===== COPY BUTTON =====
export function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <button onClick={handleCopy} style={{
      padding: '4px 10px', background: copied ? T.greenSoft : '#F1F5F9',
      border: `1px solid ${copied ? T.blueBorder : T.border}`, borderRadius: 6,
      fontSize: 11, fontWeight: 600, color: copied ? T.blue : T.textMuted,
      cursor: 'pointer', fontFamily: T.font,
    }}>{copied ? '✓ Nusxalandi' : '📋 Nusxalash'}</button>
  );
}

// ===== COLOR BUTTON =====
export function ColorBtn({ children, color = T.blue, onClick, disabled, style = {} }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '12px 24px', background: disabled ? '#E5E7EB' : color,
      color: disabled ? '#9CA3AF' : '#fff', border: 'none', borderRadius: 10,
      fontSize: 14, fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
      fontFamily: T.font, transition: 'all 0.2s', ...style,
    }}>{children}</button>
  );
}

// ===== CARD =====
export function Card({ children, padding = 20, style = {} }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 16, padding,
      border: `1px solid ${T.border}`, boxShadow: T.shadow, ...style,
    }}>{children}</div>
  );
}

// ===== PAGE HEADER =====
export function PageHeader({ title, subtitle, icon, action }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 24, flexWrap: 'wrap', gap: 16,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {icon && <span style={{ fontSize: 28 }}>{icon}</span>}
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: T.text, margin: 0 }}>{title}</h1>
          {subtitle && <p style={{ fontSize: 14, color: T.textMuted, margin: '4px 0 0', fontWeight: 500 }}>{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ===== EMPTY STATE =====
export function EmptyState({ icon, title, description, action }) {
  return (
    <div style={{
      textAlign: 'center', padding: '60px 20px',
      background: '#fff', borderRadius: 20, boxShadow: T.shadow,
    }}>
      <div style={{ fontSize: 56, marginBottom: 16, opacity: 0.4 }}>{icon}</div>
      <h3 style={{ fontSize: 20, fontWeight: 900, color: T.text, marginBottom: 8 }}>{title}</h3>
      <p style={{ fontSize: 15, color: T.textMuted, marginBottom: 24 }}>{description}</p>
      {action}
    </div>
  );
}

// ===== LOADING =====
export function Loading({ text = 'Yuklanmoqda...' }) {
  return (
    <div style={{ textAlign: 'center', padding: 60, color: T.textMuted, fontWeight: 600 }}>
      <div style={{ fontSize: 32, marginBottom: 12, animation: 'spin 1s linear infinite' }}>⏳</div>
      {text}
    </div>
  );
}

// ===== STAT CARD =====
export function StatCard({ icon, label, value, color = T.blue, bg, border }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 14, padding: 18,
      border: `1px solid ${border || T.border}`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        <span style={{
          fontSize: 11, fontWeight: 700, color,
          background: bg || `${color}15`, padding: '3px 10px', borderRadius: 12,
        }}>{label}</span>
      </div>
      <div style={{ fontSize: 32, fontWeight: 900, color }}>{value}</div>
    </div>
  );
}

// ===== INPUT =====
export function Input({ label, error, ...props }) {
  return (
    <div style={{ marginBottom: 16 }}>
      {label && (
        <label style={{
          fontSize: 13, fontWeight: 700, color: T.textSec,
          display: 'block', marginBottom: 6,
        }}>{label}</label>
      )}
      <input
        {...props}
        style={{
          width: '100%', padding: '12px 16px',
          background: T.bg, border: `2px solid ${error ? T.coral : T.border}`,
          borderRadius: 12, fontSize: 15, fontFamily: T.font, fontWeight: 600,
          color: T.text, outline: 'none', boxSizing: 'border-box',
          transition: 'all 0.2s', ...props.style,
        }}
        onFocus={(e) => { e.target.style.borderColor = T.blue; e.target.style.boxShadow = `0 0 0 3px ${T.blue}20`; }}
        onBlur={(e) => { e.target.style.borderColor = T.border; e.target.style.boxShadow = 'none'; }}
      />
      {error && <div style={{ fontSize: 12, color: T.coral, marginTop: 4, fontWeight: 600 }}>{error}</div>}
    </div>
  );
}

// ===== MODAL =====
export function Modal({ open, onClose, title, children, maxWidth = 480 }) {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 200, padding: 20,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth,
        maxHeight: '90vh', overflow: 'auto', boxShadow: T.shadowModal,
      }}>
        <div style={{
          padding: '20px 24px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 800, color: T.text, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{
            width: 36, height: 36, borderRadius: 10,
            background: T.bg, border: `1px solid ${T.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', fontSize: 16, color: T.textMuted,
          }}>✕</button>
        </div>
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
}
